import { useEffect, useRef, useState } from 'react';
import type { FieldPathValue, FieldValues, Path, UseFormSetValue } from 'react-hook-form';

declare global {
  interface ZoomInfoThis {
    inputs: Array<{
      mappedField: string;
      nameAttr: string;
    }>;

    onMatch?: (this: ZoomInfoThis, data: Record<string, string>) => void;
    onReady?: (this: ZoomInfoThis) => void;
    fcRetrigger?: () => void;
  }
  interface Window {
    _zi_fc: ZoomInfoThis;
  }
}

export type useFormCompleteArgs<TFieldValues extends FieldValues> = {
  setValue: UseFormSetValue<TFieldValues>;
  selectMappings?: Partial<{
    [key in Path<TFieldValues> as NonNullable<FieldPathValue<TFieldValues, key>> extends {
      label: string;
      value: string;
    }
      ? key
      : never]: ReadonlyArray<{
      label: string;
      value: string;
    }>;
  }>;
};

const useFormComplete = <TFieldValues extends FieldValues>({
  setValue,
  selectMappings = {},
}: useFormCompleteArgs<TFieldValues>) => {
  const selectMappingsRef = useRef(selectMappings);
  const loadingAttempsRef = useRef(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  /**
   * Keep the ref in sync with the latest selectMappings
   * without retriggering the whole ZoomInfo effect
   */
  useEffect(() => {
    selectMappingsRef.current = selectMappings;
  }, [selectMappings]);

  /**
   * Try and load FormComplete for ~5 seconds.
   * If it doesn't load, we stop trying and consider FormComplete as not working / not loaded
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const interval = setInterval(() => {
      if (loadingAttempsRef.current > 10) {
        clearInterval(interval);
        setHasLoaded(true);
        console.warn('FormComplete did not load after 10 attempts.');
        return;
      }

      if (typeof window._zi_fc?.fcRetrigger === 'function') {
        window._zi_fc.fcRetrigger();
        clearInterval(interval);
      } else {
        loadingAttempsRef.current++;
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window._zi_fc = window._zi_fc || {};

    const findSelectOption = (nameAttr: keyof typeof selectMappings, desiredValue: string) =>
      selectMappingsRef.current[nameAttr]!.find((option) => option.label === desiredValue);

    const setValuesOnInputs = (
      data: Record<string, string>,
      inputs: Window['_zi_fc']['inputs']
    ) => {
      inputs.forEach(function (input) {
        /* Skip email field. */
        if (input.mappedField === 'email') {
          return;
        }

        /* Locate and update field value. */
        const field = document.querySelector<HTMLInputElement>(
          'input[name="' + input.nameAttr + '"]'
        );
        const fieldValue = data[input.nameAttr] || '';
        if (!field) {
          return;
        }
        field.value = fieldValue;
        field.dispatchEvent(new Event('change', { bubbles: true }));

        if (input.nameAttr in selectMappingsRef.current) {
          const option = findSelectOption(
            input.nameAttr as keyof typeof selectMappings,
            data[input.nameAttr]
          );
          setValue(
            input.nameAttr as Path<TFieldValues>,
            option as FieldPathValue<TFieldValues, Path<TFieldValues>>
          );
        }
      });
    };

    const onMatchClone = window._zi_fc.onMatch;
    const onReadyClone = window._zi_fc.onReady;

    window._zi_fc.onReady = function () {
      setHasLoaded(true);
    };

    window._zi_fc.onMatch = function (data) {
      /* Run onMatch clone first. */
      if (typeof onMatchClone === 'function') {
        onMatchClone.bind(this)(data);
      }

      /* Iterate over each FormComplete mapped field. */
      setValuesOnInputs(data, this.inputs);
    };

    return () => {
      window._zi_fc.onMatch = onMatchClone;
      window._zi_fc.onReady = onReadyClone;
    };
  }, [setValue]);

  return hasLoaded;
};

export default useFormComplete;
